import {
  AVAILABLE_MODEL_VERSIONS,
  MAX_REFERENCE_PROMPTS,
  MODEL_VERSION,
  Q2_PRO_REFERENCE_VIDEO_MAX_DURATION,
  Q2_PRO_REFERENCE_VIDEO_MAX_FILE_SIZE,
} from '@/app/constants';
import type { AllModelVersion, ElementModality, FormTabType, ModelVersion, ReferencePrompt } from '@/app/types';

export type ReferenceUploadType = 'image' | 'video' | 'audio';
export type ReferenceElementModality = Extract<ElementModality, 'text' | 'image' | 'video'>;

export type ReferenceUploadCapability = {
  maxCount: number;
  minDuration?: number;
  maxDuration?: number;
  maxFileSize?: number;
};

export type ReferenceModelCapabilities = {
  imageAndElementMaxCount: number;
  elementModalities?: readonly ReferenceElementModality[];
  uploads: Record<ReferenceUploadType, ReferenceUploadCapability>;
  videoLongDurationThreshold?: number;
  secondVideoMaxDuration?: number;
};

const Q2_PRO_IMAGE_AND_ELEMENT_MAX_COUNT = 4;
const DEFAULT_ELEMENT_MODALITIES: readonly ReferenceElementModality[] = ['text', 'image'];

const DEFAULT_REFERENCE_MODEL_CAPABILITIES: ReferenceModelCapabilities = {
  imageAndElementMaxCount: MAX_REFERENCE_PROMPTS,
  elementModalities: DEFAULT_ELEMENT_MODALITIES,
  uploads: {
    image: { maxCount: MAX_REFERENCE_PROMPTS },
    video: { maxCount: 0 },
    audio: { maxCount: 0 },
  },
};

const REFERENCE_MODEL_CAPABILITIES: Partial<Record<AllModelVersion, ReferenceModelCapabilities>> = {
  [MODEL_VERSION.Q2_PRO]: {
    imageAndElementMaxCount: Q2_PRO_IMAGE_AND_ELEMENT_MAX_COUNT,
    // undefined matches the existing workspace behavior: do not filter element modalities.
    elementModalities: undefined,
    uploads: {
      image: { maxCount: Q2_PRO_IMAGE_AND_ELEMENT_MAX_COUNT },
      video: {
        maxCount: 2,
        minDuration: 1,
        maxDuration: Q2_PRO_REFERENCE_VIDEO_MAX_DURATION,
        maxFileSize: Q2_PRO_REFERENCE_VIDEO_MAX_FILE_SIZE,
      },
      audio: { maxCount: 0 },
    },
    videoLongDurationThreshold: 5,
    secondVideoMaxDuration: 5,
  },
};

export const isReferenceVideoPrompt = (reference: ReferencePrompt): boolean => {
  if (reference.type === 'video') return true;
  if (reference.type === 'element') return reference.prompt?.modality === 'video';
  return false;
};

/**
 * 视频类参考的可播放地址，用于异步探测真实时长。
 *
 * 上传的视频自带 videoPreview；视频主体（element）的接口不返回时长，只能从第一个
 * video component 的 content 上拿 URL 再自行探测。
 */
export const getReferenceVideoUrl = (reference: ReferencePrompt): string | undefined => {
  if (reference.type === 'video') {
    return reference.prompt?.videoPreview || reference.prompt?.video;
  }

  if (reference.type === 'element' && reference.prompt?.modality === 'video') {
    return reference.prompt.components?.find((component) => component.content_type === 'video')?.content;
  }

  return undefined;
};

export const getReferenceModelCapabilities = (modelVersion: AllModelVersion): ReferenceModelCapabilities =>
  REFERENCE_MODEL_CAPABILITIES[modelVersion] ?? DEFAULT_REFERENCE_MODEL_CAPABILITIES;

export const getReferenceImageAndElementMaxCount = (modelVersion: AllModelVersion): number =>
  getReferenceModelCapabilities(modelVersion).imageAndElementMaxCount;

export const getReferenceElementModalities = (
  modelVersion: AllModelVersion,
): readonly ReferenceElementModality[] | undefined => getReferenceModelCapabilities(modelVersion).elementModalities;

export const isReferenceUploadSupported = (modelVersion: AllModelVersion, type: ReferenceUploadType): boolean =>
  getReferenceModelCapabilities(modelVersion).uploads[type].maxCount > 0;

export const isReferenceVideoSupported = (modelVersion: AllModelVersion): boolean =>
  isReferenceUploadSupported(modelVersion, 'video');

export const getReferenceVideoElementMaxCount = (modelVersion: ModelVersion): number =>
  getReferenceModelCapabilities(modelVersion).uploads.video.maxCount;

/**
 * 已探测到的视频时长（秒），按视频地址缓存。
 *
 * 视频主体的接口不返回时长，上传的视频在写回 prompt.duration 前也拿不到，两者都要靠
 * `getVideoMetadata` 异步探测。额度判断本身必须是同步的，所以探测结果统一存这里，
 * 让 `getReferenceVideoMaxCount` 能同步读到。
 */
const probedVideoDurations = new Map<string, number>();

/** 探测失败过的地址。记下来避免同一个地址被反复重试，也让调用方能区分「还没探」和「探不到」。 */
const failedVideoDurationProbes = new Set<string>();

export const setProbedReferenceVideoDuration = (videoUrl: string, duration: number): void => {
  if (!videoUrl || !(duration > 0)) return;
  failedVideoDurationProbes.delete(videoUrl);
  probedVideoDurations.set(videoUrl, duration);
};

export const markReferenceVideoDurationProbeFailed = (videoUrl: string): void => {
  if (!videoUrl || probedVideoDurations.has(videoUrl)) return;
  failedVideoDurationProbes.add(videoUrl);
};

export const hasReferenceVideoDurationProbeFailed = (videoUrl: string | undefined): boolean =>
  !!videoUrl && failedVideoDurationProbes.has(videoUrl);

export const getProbedReferenceVideoDuration = (videoUrl: string | undefined): number | undefined =>
  videoUrl ? probedVideoDurations.get(videoUrl) : undefined;

/**
 * 单个视频类参考的已知时长，未知返回 undefined。
 *
 * 优先用 prompt 上写回的真实时长，其次用探测缓存。两者都没有时返回 undefined，
 * 由调用方决定怎么兜底。
 */
export const getReferenceVideoDuration = (reference: ReferencePrompt): number | undefined => {
  if (reference.type === 'video' && reference.prompt?.duration) {
    return reference.prompt.duration;
  }

  return getProbedReferenceVideoDuration(getReferenceVideoUrl(reference));
};

/**
 * 判断单个视频是否属于「短视频」（可以和另一段共用两个槽位）。
 *
 * 阈值是严格小于：5.0s 算长视频，只能单独用一个槽位。这与文案「2 个 5s 内 / <5s each」
 * 以及线上行为一致。
 *
 * 注意这里刻意不加 `getMaxVideoDurationWithTolerance` 的 +0.4s 容差——那个容差是给上传
 * 校验用的（Vidu 生成的 5s 视频读出来可能是 5.4s），加在这里会让 5.0s 变成短视频，
 * 直接改掉线上的槽位行为。
 */
const isReferenceVideoShort = (duration: number, threshold: number): boolean => duration < threshold;

export const getReferenceVideoMaxCount = (
  modelVersion: string | undefined,
  referencePrompts: ReferencePrompt[],
): number => {
  if (!modelVersion || !isReferenceVideoSupported(modelVersion as AllModelVersion)) {
    return 0;
  }

  const capabilities = getReferenceModelCapabilities(modelVersion as AllModelVersion);
  const videoCapability = capabilities.uploads.video;
  const videoRefs = referencePrompts.filter(isReferenceVideoPrompt);
  if (videoRefs.length === 0) {
    return Math.min(1, videoCapability.maxCount);
  }

  const { videoLongDurationThreshold } = capabilities;
  if (videoLongDurationThreshold === undefined) {
    return videoCapability.maxCount;
  }

  // 上传的视频和视频主体都用真实时长判断（主体的时长靠 URL 探测后缓存）。
  //
  // 时长未知时（上传中、或探测失败）按保守处理，只给 1 个槽位：宁可第二个入口晚一点出现，
  // 也不能先放开、等拿到时长发现超过 5s 再把用户已经加进来的第二段撤掉。
  const firstVideoDuration = getReferenceVideoDuration(videoRefs[0]);
  const isFirstVideoLong =
    firstVideoDuration === undefined || !isReferenceVideoShort(firstVideoDuration, videoLongDurationThreshold);

  return isFirstVideoLong ? 1 : videoCapability.maxCount;
};

/**
 * 一次性选中多个视频类参考（典型场景：主体选择器多选）时，校验时长组合是否满足
 * 「1 个 5-8s 或 2 个 5s 内」。
 *
 * 不能假设「主体一定 <= 5s」——用户自建的主体虽然被限制在 5s，但官方预置主体
 * （type: 'preset'）由后端配置，不经过前端 uploader，时长没有保证。
 *
 * @param durations 待加入的视频时长（秒）；`undefined` 表示时长未知
 * @returns 允许加入时返回 ok，否则带上拒绝原因
 */
export const validateReferenceVideoDurationCombination = (
  modelVersion: string | undefined,
  existingReferencePrompts: ReferencePrompt[],
  durations: Array<number | undefined>,
): { ok: true } | { ok: false; reason: 'exceedsCount' | 'unknownDuration' | 'tooLongForMultiple' } => {
  if (durations.length === 0) return { ok: true };

  const capabilities = getReferenceModelCapabilities(modelVersion as AllModelVersion);
  const { videoLongDurationThreshold } = capabilities;
  const existingVideoCount = existingReferencePrompts.filter(isReferenceVideoPrompt).length;
  const totalCount = existingVideoCount + durations.length;

  if (totalCount > capabilities.uploads.video.maxCount) {
    return { ok: false, reason: 'exceedsCount' };
  }

  // 没有长短之分的模型（没配阈值）只看数量。
  if (videoLongDurationThreshold === undefined) {
    return { ok: true };
  }

  // 最终会有多段视频时，每一段都必须在阈值内——包括已经在列表里的那些。
  if (totalCount > 1) {
    const existingDurations = existingReferencePrompts
      .filter(isReferenceVideoPrompt)
      .map((reference) => getReferenceVideoDuration(reference));

    for (const duration of [...existingDurations, ...durations]) {
      if (duration === undefined) {
        return { ok: false, reason: 'unknownDuration' };
      }
      if (!isReferenceVideoShort(duration, videoLongDurationThreshold)) {
        return { ok: false, reason: 'tooLongForMultiple' };
      }
    }
  }

  return { ok: true };
};

export const getReferenceMediaDropArgs = (
  modelVersion: AllModelVersion,
  referencePrompts: ReferencePrompt[],
  options?: {
    minVideoDuration?: number;
    maxVideoDuration?: number;
  },
): {
  maxImageCount: number;
  maxVideoCount: number;
  minVideoDuration: number;
  maxVideoDuration: number;
  maxVideoSize?: number;
  dropDisabled: boolean;
} => {
  const capabilities = getReferenceModelCapabilities(modelVersion);
  const videoCapability = capabilities.uploads.video;
  const configuredMinVideoDuration = options?.minVideoDuration ?? videoCapability.minDuration ?? 1;
  const configuredMaxVideoDuration =
    options?.maxVideoDuration ?? videoCapability.maxDuration ?? Q2_PRO_REFERENCE_VIDEO_MAX_DURATION;

  const videoPrompts = referencePrompts.filter(isReferenceVideoPrompt);
  const imageAndElementPrompts = referencePrompts.filter((item) => !isReferenceVideoPrompt(item));

  const getVideoUploadConfig = (): { minDuration: number; maxDuration: number; maxVideoSlotsTotal: number } => {
    const maxVideoSlotsTotal = getReferenceVideoMaxCount(modelVersion, referencePrompts);
    const currentVideoIndex = videoPrompts.length;

    if (currentVideoIndex === 0) {
      return {
        minDuration: configuredMinVideoDuration,
        maxDuration: videoCapability.maxDuration ?? configuredMaxVideoDuration,
        maxVideoSlotsTotal,
      };
    }

    if (currentVideoIndex === 1) {
      if (maxVideoSlotsTotal <= 1) {
        return {
          minDuration: configuredMinVideoDuration,
          maxDuration: configuredMaxVideoDuration,
          maxVideoSlotsTotal: 1,
        };
      }

      return {
        minDuration: configuredMinVideoDuration,
        maxDuration: capabilities.secondVideoMaxDuration ?? configuredMaxVideoDuration,
        maxVideoSlotsTotal,
      };
    }

    return {
      minDuration: configuredMinVideoDuration,
      maxDuration: configuredMaxVideoDuration,
      maxVideoSlotsTotal,
    };
  };

  const { minDuration, maxDuration, maxVideoSlotsTotal } = getVideoUploadConfig();
  const maxImageCount = Math.max(0, capabilities.imageAndElementMaxCount - imageAndElementPrompts.length);
  const maxVideoCount = Math.max(0, maxVideoSlotsTotal - videoPrompts.length);

  return {
    maxImageCount,
    maxVideoCount,
    minVideoDuration: minDuration,
    maxVideoDuration: maxDuration,
    maxVideoSize: videoCapability.maxFileSize,
    dropDisabled: imageAndElementPrompts.length >= capabilities.imageAndElementMaxCount && maxVideoCount <= 0,
  };
};

export const getReferenceModelCapabilityKey = (modelVersion: AllModelVersion): string => {
  const capabilities = getReferenceModelCapabilities(modelVersion);
  const supportVideo = capabilities.uploads.video.maxCount > 0 ? 1 : 0;
  return `${capabilities.imageAndElementMaxCount}_${supportVideo}`;
};

export const getDefaultModelVersionForReferenceMedia = (
  type: Exclude<FormTabType, 'scene'>,
  uploadType: ReferenceUploadType,
): ModelVersion | undefined =>
  AVAILABLE_MODEL_VERSIONS[type].find((modelVersion) => isReferenceUploadSupported(modelVersion, uploadType));
